import validator from "validator";
import moment from "moment";
import { isValidPhone } from "../../../../utils/validators";
import { normalizePhoneNumber } from "../../../../utils";
import { teacherRegisterDetailsConfig } from "../../../privateClasses/constants";
import { DetailType } from "../../../privateClasses/types";
import { HTMLTagRegEx } from "../../../../constants";

const requiredTypes: DetailType[] = teacherRegisterDetailsConfig
  .filter((config) => config.isRequired)
  .map((config) => config.type);

export function registerStudentFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.studentProfile) {
    errors.studentProfile = {
      birth_date: "Birth date is required.",
      country: "Country is required.",
      city: "City is required.",
      citizenship: "Citizenship is required.",
      gender: "Gender is required.",
    };
  } else {
    errors.studentProfile = {};

    const date = values.studentProfile.birth_date;
    const maxAge = 70;
    const minAge = 5;
    const allowedMaxDate = moment().subtract(minAge, "years").startOf("day");
    const allowedMinDate = moment().subtract(maxAge, "years").startOf("day");

    if (!date) {
      errors.studentProfile.birth_date = "Birth date is required.";
    } else if (!date.isValid()) {
      errors.studentProfile.birth_date = "Invalid Date Format";
    } else if (date >= allowedMaxDate) {
      errors.studentProfile.birth_date = `Minimum allowed age is ${minAge} years`;
    } else if (date <= allowedMinDate) {
      errors.studentProfile.birth_date = `Maximum allowed age is ${maxAge} years`;
    }

    if (!values.studentProfile.country) {
      errors.studentProfile.country = "Country is required.";
    }

    if (!values.studentProfile.city) {
      errors.studentProfile.city = "City is required.";
    } else if (values.studentProfile.city.trim().length < 2) {
      errors.studentProfile.city = "City must have minimum 2 characters.";
    } else if (values.studentProfile.city.trim().length > 50) {
      errors.studentProfile.city =
        "City maximum length should be 50 characters.";
    }

    if (!values.studentProfile.citizenship) {
      errors.studentProfile.citizenship = "Citizenship is required.";
    }

    if (!values.studentProfile.gender) {
      errors.studentProfile.gender = "Gender is required.";
    }
  }

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}

export function registerTeacherFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.teacherProfile) {
    errors.teacherProfile = {
      country: "Country is required.",
      gender: "Gender is required.",
      level: "Level is required.",
      subject: "Subject is required.",
      teachingLanguage: "Teaching Language is required.",
    };
  } else {
    errors.teacherProfile = {};

    if (!values.teacherProfile.country) {
      errors.teacherProfile.country = "Country is required.";
    }

    if (!values.teacherProfile.gender) {
      errors.teacherProfile.gender = "Gender is required.";
    }

    if (!values.teacherProfile.level?.length) {
      errors.teacherProfile.level = "Level is required.";
    }

    if (!values.teacherProfile.subject?.length) {
      errors.teacherProfile.subject = "Subject is required.";
    }

    if (!values.teacherProfile.teachingLanguage?.length) {
      errors.teacherProfile.teachingLanguage = "Teaching Language is required.";
    }
  }

  const details = values.teacherProfile?.details || {};

  requiredTypes.forEach((type) => {
    if (
      !details[`type_${type}`] ||
      !details[`type_${type}`].replace(HTMLTagRegEx, "").trim().length
    ) {
      if (!errors.teacherProfile.details) {
        errors.teacherProfile.details = {};
      }

      errors.teacherProfile.details[`type_${type}`] = "This field is required.";
    }
  });

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}

export function registerAgentFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.agentProfile) {
    errors.agentProfile = {
      name: "Company name is required.",
      country: "Country is required.",
      title: "Position is required.",
    };
  } else {
    errors.agentProfile = {};

    if (!values.agentProfile.name) {
      errors.agentProfile.name = "Company name is required.";
    } else if (values.agentProfile.name.trim().length < 3) {
      errors.agentProfile.name = "Company name must have minimum 3 characters.";
    } else if (values.agentProfile.name.trim().length > 50) {
      errors.agentProfile.name =
        "Company name maximum length should be 50 characters.";
    }

    if (!values.agentProfile.country) {
      errors.agentProfile.country = "Country is required.";
    }

    if (
      values.agentProfile?.website &&
      !validator.isURL(values.agentProfile?.website?.trim())
    ) {
      errors.agentProfile.website = "Website must be a valid url.";
    } else if (values.agentProfile?.website?.trim().length < 3) {
      errors.agentProfile.website = "Website must have minimum 3 characters.";
    } else if (values.agentProfile?.website?.trim().length > 50) {
      errors.agentProfile.website =
        "Website maximum length should be 50 characters.";
    }

    if (!values.agentProfile.title) {
      errors.agentProfile.title = "Position is required.";
    } else if (values.agentProfile.title.trim().length < 3) {
      errors.agentProfile.title = "Position must have minimum 3 characters.";
    } else if (values.agentProfile.title.trim().length > 50) {
      errors.agentProfile.title =
        "Position maximum length should be 50 characters.";
    }
  }

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}

export function registerParentFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}

export function registerSchoolFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Representative name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Representative name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Representative name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.schoolProfile?.school) {
    errors.schoolProfile = {
      school: {
        name: "School name is required.",
        country: "Country is required.",
        type: "Education type is required.",
        website: "Website is required.",
      },
    };
  } else {
    errors.schoolProfile = {
      school: {},
    };

    if (!values.schoolProfile?.school.name) {
      errors.schoolProfile.school.name = "School name is required.";
    } else if (values.schoolProfile?.school.name.trim().length < 5) {
      errors.schoolProfile.school.name =
        "School name must have minimum 5 characters.";
    } else if (values.schoolProfile?.school.name.trim().length > 50) {
      errors.schoolProfile.school.name =
        "School name maximum length should be 50 characters.";
    }

    if (!values.schoolProfile?.school.country) {
      errors.schoolProfile.school.country = "Country is required.";
    }

    if (!values.schoolProfile?.school.website) {
      errors.schoolProfile.school.website = "Website is required.";
    } else if (!validator.isURL(values.schoolProfile.school.website.trim())) {
      errors.schoolProfile.school.website = "Website must be a valid url.";
    }

    if (!values.schoolProfile?.school.type) {
      errors.schoolProfile.school.type = "Education type is required.";
    }
  }

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values.image) {
    errors.image = "School logo is required.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}

export function registerServiceProviderFormValidator(values, openWarningModal) {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Representative name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Representative name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Representative name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.serviceProviderProfile?.serviceProvider) {
    errors.serviceProviderProfile = {
      serviceProvider: {
        name: "Service provider name is required.",
        country: "Country is required.",
        website: "Website is required.",
      },
    };
  } else {
    errors.serviceProviderProfile = {
      serviceProvider: {},
    };

    if (!values.serviceProviderProfile?.serviceProvider.name) {
      errors.serviceProviderProfile.serviceProvider.name =
        "Service provider name is required.";
    } else if (
      values.serviceProviderProfile?.serviceProvider.name.trim().length < 5
    ) {
      errors.serviceProviderProfile.serviceProvider.name =
        "Service provider name must have minimum 5 characters.";
    } else if (
      values.serviceProviderProfile?.serviceProvider.name.trim().length > 50
    ) {
      errors.serviceProviderProfile.serviceProvider.name =
        "Service provider name maximum length should be 50 characters.";
    }

    if (!values.serviceProviderProfile?.serviceProvider.country) {
      errors.serviceProviderProfile.serviceProvider.country =
        "Country is required.";
    }

    if (!values.serviceProviderProfile?.serviceProvider.website) {
      errors.serviceProviderProfile.serviceProvider.website =
        "Website is required.";
    } else if (
      !validator.isURL(
        values.serviceProviderProfile.serviceProvider.website.trim(),
      )
    ) {
      errors.serviceProviderProfile.serviceProvider.website =
        "Website must be a valid url.";
    }
  }

  if (!values.password) {
    errors.password = "Password is required.";
  } else if (values.password.length < 8) {
    errors.password = "Password must have minimum 8 characters.";
  } else if (values.password.length > 15) {
    errors.password = "Password maximum length should be 15 characters.";
  }

  if (!values.password_confirmation) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (
    values.password_confirmation &&
    values.password !== values.password_confirmation
  ) {
    errors.password = "Passwords do not match.";
    errors.password_confirmation = "Passwords do not match.";
  }

  if (!values.image) {
    errors.image = "Service provider logo is required.";
  }

  if (!values._isTermsAgreed) {
    errors._isTermsAgreed = "Terms & Conditions are required.";
    openWarningModal();
  }

  return errors;
}
